import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type {
	ConfluenceHeaderImageGenerationResponse,
	ConfluencePageEmojiSuggestionError,
	ConfluencePageEmojiSuggestionErrorReason,
	ConfluencePageEmojiSuggestionResponse,
} from '@atlassian/convo-ai-api';

export type AIHeaderImageStatus =
	| 'NOT_LOADING'
	| 'LOADING'
	| 'LOADING_WITH_ACCEPTANCE_FLOW'
	| 'SUCCESSFULLY_SET'
	| 'ERROR';

/**
 * Available theme values for AI header image generation
 */
export const IMAGE_GENERATION_THEMES = {
	photorealistic: 'photorealistic',
	abstract: 'abstract',
	watercolors: 'watercolors',
	graphic_design: 'graphic_design',
	landscape: 'landscape',
	sketch: 'sketch',
	threeD: '3d',
} as const;

export type ImageGenerationTheme =
	(typeof IMAGE_GENERATION_THEMES)[keyof typeof IMAGE_GENERATION_THEMES];

export const IMAGE_GENERATION_MODELS = {
	auto: 'auto',
	fast: 'fast',
	high: 'high',
	max: 'max',
} as const;

export type ImageGenerationModel =
	(typeof IMAGE_GENERATION_MODELS)[keyof typeof IMAGE_GENERATION_MODELS];

export type GenerateImageProps = {
	adfContent: string;
	aspectRatio?: string;
	imageGenerationModel?: ImageGenerationModel;
	showAcceptanceFlow?: boolean;
	theme?: ImageGenerationTheme;
	useRawPrompt?: boolean;
};

type GenericBaseParams = {
	aspectRatio?: string;
	contentId: string;
	imageGenerationModel?: ImageGenerationModel;
	theme?: string;
	useRawPrompt: boolean;
};

export type GenerateImageErrorParams = GenericBaseParams & {
	error: Error;
};

export type GenerateImageEventCallbacks = {
	onGenerateAbort?: (params: GenericBaseParams) => void;
	onGenerateError?: (params: GenerateImageErrorParams) => void;
	onGenerateStart?: (params: GenericBaseParams) => void;
	onGenerateSuccess?: (params: GenericBaseParams) => void;
};

export type UseAIGeneratedCoverImageProps = {
	contentId: string;
	/**
	 * Optional callbacks for tracking image generation events (e.g., experience tracking)
	 */
	eventCallbacks?: GenerateImageEventCallbacks;
};

export type UseAIGeneratedCoverImageReturn = {
	aiHeaderImageFileId?: string;
	aiHeaderImageStatus?: AIHeaderImageStatus;
	data: ConfluenceHeaderImageGenerationResponse | null;
	error: string | null;
	fakeGenerateImage: () => void;
	generateImage: (props: GenerateImageProps) => Promise<string | undefined>;
	loading: boolean;
	setAIHeaderImageStatus: (status: AIHeaderImageStatus) => void;
};

export type UseRemixImageGenerationProps = {
	cloudId: string;
	contentId: string;
	createEditorMediaProvider?: (options: {
		contentId: string;
		contentType: string;
		isExternalShare: boolean;
	}) => Promise<MediaProvider>;
	/**
	 * Optional callbacks for tracking image generation events (e.g., experience tracking).
	 */
	eventCallbacks?: GenerateImageEventCallbacks;
	/**
	 * Status to expose after Remix has generated and copied the image.
	 *
	 * Consumers that need to persist the copied image before marking it as applied can use
	 * `NOT_LOADING`. The default preserves the existing Company Hub behaviour.
	 */
	successfulGenerationStatus?: 'NOT_LOADING' | 'SUCCESSFULLY_SET';
	touchpointSource?: string;
	userId: string | null;
};

export type UseRemixImageGenerationReturn = {
	aiHeaderImageFileId?: string;
	aiHeaderImageStatus: AIHeaderImageStatus;
	error: string | null;
	fakeGenerateImage: () => void;
	generateImage: (props: GenerateImageProps) => Promise<string | undefined>;
	loading: boolean;
	setAIHeaderImageStatus: (status: AIHeaderImageStatus) => void;
};

// ============================================================================
// AI Page Emoji Suggestion (AIFC)
// ============================================================================

/**
 * Client-side state-machine status for the AIFC page emoji suggestion flow.
 *
 * Maps loosely from the BE `ConfluencePageEmojiSuggestionStatus`:
 *  - `NOT_LOADING`      — idle (initial or post-reset)
 *  - `LOADING`          — request in flight
 *  - `SUCCESSFULLY_SET` — BE returned `suggested` or `default` and we applied `emojiId`
 *  - `NO_SUGGESTION`    — BE returned `no_suggestion` (RAI moderation). Render nothing.
 *  - `INVALID_INPUT`    — FE-side short-circuit (blank title); did not call BE
 *  - `ERROR`            — BE returned `error`, or non-2xx response treated as `api_error`
 */
export type AIPageEmojiSuggestionStatus =
	| 'NOT_LOADING'
	| 'LOADING'
	| 'SUCCESSFULLY_SET'
	| 'NO_SUGGESTION'
	| 'INVALID_INPUT'
	| 'ERROR';

export type SuggestPageEmojiProps = {
	/**
	 * Plain-text page title. Will be trimmed client-side; blank titles short-circuit
	 * to `INVALID_INPUT` and no BE call is made.
	 */
	title: string;
	/**
	 * Optional BCP-47 locale hint passed through to the BE for telemetry only.
	 */
	locale?: string;
};

export type SuggestPageEmojiBaseParams = {
	contentId: string;
	titleLength: number;
	locale?: string;
};

export type SuggestPageEmojiSuccessParams = SuggestPageEmojiBaseParams & {
	status: 'suggested' | 'default';
	emojiId: string;
};

export type SuggestPageEmojiNoSuggestionParams = SuggestPageEmojiBaseParams & {
	status: 'no_suggestion';
};

export type SuggestPageEmojiErrorParams = SuggestPageEmojiBaseParams & {
	error: Error;
	errorReason?: ConfluencePageEmojiSuggestionErrorReason;
};

export type SuggestPageEmojiEventCallbacks = {
	onSuggestStart?: (params: SuggestPageEmojiBaseParams) => void;
	onSuggestSuccess?: (params: SuggestPageEmojiSuccessParams) => void;
	onSuggestNoSuggestion?: (params: SuggestPageEmojiNoSuggestionParams) => void;
	onSuggestError?: (params: SuggestPageEmojiErrorParams) => void;
	onSuggestAbort?: (params: SuggestPageEmojiBaseParams) => void;
};

export type UseAIPageEmojiSuggestionProps = {
	contentId: string;
	/**
	 * Optional callbacks for tracking emoji suggestion events (e.g. experience tracking).
	 */
	eventCallbacks?: SuggestPageEmojiEventCallbacks;
};

export type UseAIPageEmojiSuggestionReturn = {
	/**
	 * Trigger an AIFC emoji suggestion request. Resolves to the suggested `emojiId` when
	 * the BE returns `suggested` or `default`; resolves to `undefined` for `no_suggestion`,
	 * `error`, aborted requests, or blank-title short-circuits.
	 *
	 * Safe to call multiple times — in-flight requests are aborted before a new one starts.
	 */
	suggestEmoji: (props: SuggestPageEmojiProps) => Promise<string | undefined>;
	/**
	 * Resolved `emojiId` from the latest `suggested` or `default` response. `undefined` while
	 * loading, after a `no_suggestion`, an error, or before any call has been made.
	 */
	aiEmojiId?: string;
	/**
	 * Model rationale from the latest response. Telemetry/debug only — never render.
	 */
	aiEmojiReasoning?: string;
	aiEmojiStatus: AIPageEmojiSuggestionStatus;
	/**
	 * Raw parsed BE response, useful for telemetry. `null` until the first response arrives.
	 */
	data: ConfluencePageEmojiSuggestionResponse | null;
	/**
	 * Raw error info from the latest failed response, or `null` if there was no error.
	 */
	error: ConfluencePageEmojiSuggestionError | null;
	/**
	 * `true` when the latest error is safely retryable (i.e. `error_reason === 'api_error'`).
	 * Always `false` for `safety` and `invalid_input`.
	 */
	isRetryable: boolean;
	loading: boolean;
	/**
	 * Reset state back to `NOT_LOADING`, clearing any cached emoji/error/data. Does not abort
	 * an in-flight request — call `suggestEmoji` again to abort + retry.
	 */
	reset: () => void;
};
